import React, {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useMount } from 'react-use'
import ActivityLogPanel from 'src/components/molecules/ActivityLogPanel'
import { ToastTriggerContext } from 'src/context/toast.context'
import { ActivityLogEntityType } from 'src/domain/activityLog'
import { isActivityLogEnabled } from 'src/domain/org'
import { isFinalizable } from 'src/domain/reportStatus'
import {
  AdminAdminSalesReportManagerEditorSalesReportManagerRoles,
  isOneOfRoles,
} from 'src/domain/role'
import { useActivityLogPanel } from 'src/hooks/useActivityLogPanel'
import useAppTitle from 'src/hooks/useAppTitle'
import { EventType, useBigQuery } from 'src/hooks/useBigQuery'
import Path, { TenantPathParams } from 'src/routes/path'
import {
  decrementSelectedTenantIndex,
  DEFAULT_DATE,
  incrementSelectedTenantIndex,
} from 'src/slices/confirmSalesReport/confirmSalesReportSlice'
import {
  clearPutDefiniteValues,
  PutDefiniteValue,
  putDefiniteValues,
} from 'src/slices/definiteValues/definiteValuesSlice'
import { putMessage } from 'src/slices/message/messageSlice'
import {
  ActivityLogScopeTypeEnum,
  ReceiptImage,
  Store,
  Tenant,
} from 'src/slices/services/api'
import { getStore, selectStoreByCode } from 'src/slices/stores/storesSlice'
import {
  getTenant,
  selectTenantByCode,
  selectTenantStateByCode,
} from 'src/slices/tenants/tenantsSlice'
import {
  clearGetTenantSalesReportByParams,
  getTenantSalesReports,
  postNotificationToTenant,
  selectTenantSalesReportByParams,
  selectTenantSalesReportStateByParams,
} from 'src/slices/tenantSalesReports/tenantSalesReportsSlice'
import { clearTenantSalesReportSummaries } from 'src/slices/tenantSalesReportSummaries/tenantSalesReportSummariesSlice'
import { useAppDispatch, useAppSelector } from 'src/store'
import { formatDateWithDayOfWeek } from 'src/utils/date'
import { getColumns } from '../../constants'
import TenantSalesReportTemplate from '../../templates/TenantSalesReport'
import { FormNameEnum, FormTypesTenantSalesReport, Register } from '../../type'
import Presenter from './presenter'

export const DefaultFieldValues: FormTypesTenantSalesReport = {
  registers: [],
  selectSalesDate: '',
  manageMessage: '',
  filterForMismatch: false,
}

const TenantSalesReport: React.FC = (): ReactElement => {
  useAppTitle('売上報告詳細')
  const navigate = useNavigate()
  const { orgCode, storeCode, tenantCode } =
    useParams<TenantPathParams>() as TenantPathParams
  const { sendReportCheckEvent } = useBigQuery()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const role = useAppSelector((state) => state.auth.currentAuthorization?.role)
  const toastContext = useContext(ToastTriggerContext)

  const { selectedTenantIndex, selectedTenantCodes, date } = useAppSelector(
    (state) => state.forms.confirmSalesReport
  )
  const params = useMemo(() => {
    return {
      orgCode,
      date: date || DEFAULT_DATE, // FIXME: ここでdateが無い場合はhomeにリダイレクトのほうが良さそう。
    }
  }, [date, orgCode])

  // entity
  const tenant: Tenant | undefined = useAppSelector(
    selectTenantByCode({ orgCode, storeCode, tenantCode })
  )
  const store: Store | undefined = useAppSelector(
    selectStoreByCode({ orgCode, storeCode })
  )
  const tenantSalesReport = useAppSelector(
    selectTenantSalesReportByParams({
      orgCode,
      storeCode,
      tenantCode,
      date: params.date,
    })
  )

  // 確定履歴（activity_log）はテナント売上日単位で取得する。
  // scopeId は売上日ID（tenantSalesReport.id）。未取得時は scopeId=undefined で fetch されない。
  const {
    isOpen: isLogPanelOpen,
    open: openLogPanel,
    close: closeLogPanel,
    logs: activityLogs,
    isLoading: isActivityLogsLoading,
    isLoadingMore: isActivityLogsLoadingMore,
    hasMore: hasMoreActivityLogs,
    dateRangeFilter: activityLogDateRangeFilter,
    errorMessage: activityLogError,
    onLoadMore: handleLoadMoreActivityLogs,
  } = useActivityLogPanel({
    orgCode,
    entityType: ActivityLogEntityType.SalesReport,
    scopeType: ActivityLogScopeTypeEnum.TenantsSalesDate,
    scopeId: tenantSalesReport?.id,
    baseDate: params.date,
  })

  const defaultManageMessage = useMemo(() => {
    return tenantSalesReport?.manageMessage ?? ''
  }, [tenantSalesReport])

  // Select Date Form
  const formMethods = useForm<FormTypesTenantSalesReport>({
    defaultValues: {
      ...DefaultFieldValues,
      manageMessage: defaultManageMessage,
    },
  })

  useEffect(() => {
    formMethods.setValue(FormNameEnum.manageMessage, defaultManageMessage || '')
  }, [defaultManageMessage, formMethods, tenantSalesReport?.id])

  const registers: Register[] = useMemo(() => {
    return Presenter.convertToRegisters(
      tenantSalesReport?.registers ?? [],
      orgCode
    )
  }, [tenantSalesReport?.registers, orgCode])

  const selectOptions = useMemo(() => {
    return Presenter.genSelectDateOptions(tenantSalesReport)
  }, [tenantSalesReport])

  const reportMessages = useMemo(() => {
    return Presenter.genReportMessages(tenantSalesReport, orgCode)
  }, [tenantSalesReport, orgCode])

  const watchSelectDate = formMethods.watch(FormNameEnum.selectSalesDate)
  const [selectedResentImage, setSelectedResentImage] = useState<
    ReceiptImage | undefined
  >()
  const deselectImage = () => {
    setSelectedResentImage(undefined)
  }
  useEffect(() => {
    // NOTE: watchSelectDate の初期値がundefinedの場合があるので、options から取得するように
    const selectedDate = watchSelectDate || (selectOptions[0]?.value as string)
    if (!selectedDate) return

    const newImage = Presenter.findSelectedResentImage(
      selectedDate,
      tenantSalesReport
    )

    setSelectedResentImage(newImage)
  }, [tenantSalesReport, formMethods.watch, watchSelectDate, selectOptions])
  const [confirmValidation, setConfirmValidation] = useState(false)
  const [inProgress, setInProgress] = useState(false)
  // NOTE: 確定処理の二重発火を同期的に防ぐためのフラグ。
  // inProgress(state) は再レンダリング後にしか更新されないため、確定ボタンを連打すると
  // 2回目以降のクリックが古い false を読んで通過し、次テナントの画面が初期化される前に
  // 確定APIが走って直前テナントの入力値が保存される (Issue #3685)。
  const inProgressRef = useRef(false)
  // NOTE: フォームを初期化したテナント売上日ID。入力欄(子)のeffectは親のeffectより先に走るため、
  // このeffect時点でフォームは当該テナントの値で初期化済みとみなせる。
  // 確定時にこのIDが現在のテナント売上日と一致しない場合は、初期化前の値を送る恐れがあるため中断する。
  const formInitializedForIdRef = useRef<string | undefined>(undefined)

  const updateConfirmValidation = (formValues: FormTypesTenantSalesReport) => {
    const isValid = Presenter.validateInputValues(formValues)
    setConfirmValidation(isValid)
  }

  useEffect(() => {
    const subscription = formMethods.watch((value) => {
      updateConfirmValidation(value as FormTypesTenantSalesReport)
    })
    return () => subscription.unsubscribe()
  }, [formMethods, formMethods.watch])

  // FIXME: reportを一度見て、日時管理を確認し、もう一度Reportを見るというプロセスを取ると、
  // formのsubscriptionが発火しないので、強引に実施する。
  // ただ、そもそもformのvalidationは、useformの機能としてformState.isValidで検出すればいいので
  // updateConfirmValidationじたいが不要な処理なので後々Refactorする。
  useEffect(() => {
    setTimeout(() => {
      const value = formMethods.getValues()
      updateConfirmValidation(value as FormTypesTenantSalesReport)
    }, 0)
  }, [formMethods])

  // utils
  const getDefiniteValues = (): PutDefiniteValue[] => {
    if (!tenantSalesReport) return []

    return Presenter.convertToDefiniteValues(
      tenantSalesReport.registers,
      // FIXME: getValuesに前のテナント情報が含まれているので、修正する
      formMethods.getValues(),
      orgCode
    )
  }

  // request state
  const tenantRequestState = useAppSelector(
    selectTenantStateByCode({ orgCode, storeCode, tenantCode })
  )
  const tenantSalesReportState = useAppSelector(
    selectTenantSalesReportStateByParams({
      orgCode,
      storeCode,
      tenantCode,
      date,
    })
  )

  const dispatch = useAppDispatch()
  // call GET API
  useEffect(() => {
    if (tenant === undefined && tenantRequestState.status === 'idle') {
      dispatch(getTenant({ orgCode, storeCode, tenantCode }))
    }
  }, [
    dispatch,
    orgCode,
    storeCode,
    tenant,
    tenantCode,
    tenantRequestState.status,
  ])
  useEffect(() => {
    if (store === undefined && tenantRequestState.status === 'idle') {
      dispatch(getStore({ orgCode, storeCode }))
    }
  }, [dispatch, orgCode, store, storeCode, tenantRequestState.status])
  useEffect(() => {
    if (tenantSalesReportState.status === 'idle') {
      dispatch(
        getTenantSalesReports({
          orgCode,
          storeCode,
          tenantCode,
          date: params.date,
        })
      )
    }
  }, [
    dispatch,
    orgCode,
    storeCode,
    tenantCode,
    params.date,
    tenantSalesReportState.status,
  ])

  useEffect(() => {
    if (
      tenantRequestState.status === 'loading' ||
      tenantSalesReportState.status === 'loading'
    ) {
      setIsLoading(true)
    } else {
      setIsLoading(false)
    }
  }, [tenantRequestState.status, tenantSalesReportState.status])

  // NOTE: 入力欄(子コンポーネント)のeffectが先に完了してから走るため、
  // ここでフォームが当該テナントの値で初期化済みであることを記録する (Issue #3685)。
  useEffect(() => {
    formInitializedForIdRef.current = tenantSalesReport?.id
  }, [tenantSalesReport?.id])

  const [showModal, setShowModal] = useState(false)

  useMount(() => {
    if (selectedTenantIndex === 0) {
      sendReportCheckEvent('start')
    }
  })

  // Post Request State
  const goToSummariesPage = useCallback(() => {
    sendReportCheckEvent('finish')
    dispatch(clearTenantSalesReportSummaries())
    navigate(Path.tenantSalesReportSummaries(orgCode, storeCode))
    deselectImage()
  }, [sendReportCheckEvent, dispatch, navigate, orgCode, storeCode])

  const goToNextTenant = useCallback(
    (type: EventType) => {
      // パネルを開いたまま遷移しても前テナントのレジ ID が残らないよう閉じる
      closeLogPanel()
      dispatch(
        clearGetTenantSalesReportByParams({
          orgCode,
          storeCode,
          tenantCode,
          date,
        })
      )
      dispatch(incrementSelectedTenantIndex())
      dispatch(clearPutDefiniteValues())
      deselectImage()
      const filterForMismatch = formMethods.getValues(
        FormNameEnum.filterForMismatch
      )
      // NOTE: registers を明示的に空へ戻す。省略すると直前テナントの入力値がフォームに残り、
      // 次テナントの確定でその値が送信される (Issue #3685)。
      formMethods.reset({
        ...DefaultFieldValues,
        // NOTE: DefaultFieldValues の配列参照を共有しないよう明示的に新しい配列を渡す
        registers: [],
        filterForMismatch,
      })

      if (selectedTenantIndex === selectedTenantCodes.length - 1) {
        goToSummariesPage()
        return
      }
      sendReportCheckEvent(type)

      navigate(
        Path.tenantSalesReport(
          orgCode,
          storeCode,
          selectedTenantCodes[selectedTenantIndex + 1]
        )
      )
    },
    [
      dispatch,
      orgCode,
      storeCode,
      tenantCode,
      date,
      formMethods,
      selectedTenantIndex,
      selectedTenantCodes,
      navigate,
      sendReportCheckEvent,
      goToSummariesPage,
      closeLogPanel,
    ]
  )

  const callPutMessage = async () => {
    const message = formMethods.getValues().manageMessage
    const registersArray = tenantSalesReport?.registers ?? []
    const registerIdIndex =
      registersArray.length > 0 ? registersArray.length - 1 : 0
    if (defaultManageMessage === message) return
    await dispatch(
      putMessage({
        tenantId: tenant?.id ?? '',
        salesDateId: tenantSalesReport?.id ?? '',
        // NOTE: 一番最後のレジIDとメモを紐づける
        salesDateRegisterId: registersArray[registerIdIndex]?.id ?? '',
        message,
      })
    ).unwrap()
  }

  // 「連携済み」は外部システム連携が完了した終端状態なので、全orgで確定済みへの差し戻しを抑止する (Issue #3507)
  // NOTE: サーバ側の CanSendFinalValues は連携済みからの確定を許可しているため、抑止は admin 側のみ。
  const isFinalizeBlockedByReportStatus =
    tenantSalesReport !== undefined &&
    !isFinalizable(tenantSalesReport.reportStatus)

  const disabledConfirmButton =
    !confirmValidation || inProgress || isFinalizeBlockedByReportStatus

  // Event Handler
  const handleClickConfirm = async () => {
    // NOTE: state ではなく ref で先に判定する。連打時に state の更新が間に合わず
    // 2回目以降のクリックが通過するのを防ぐ (Issue #3685)。
    if (inProgressRef.current) return

    const conditions =
      showModal ||
      !tenant ||
      !confirmValidation ||
      !tenantSalesReport ||
      inProgress ||
      isFinalizeBlockedByReportStatus
    if (conditions) return

    // NOTE: フォームが現在のテナント売上日用に初期化される前の確定を弾く。
    // 初期化前に送ると直前テナントの入力値がそのまま保存される (Issue #3685)。
    if (formInitializedForIdRef.current !== tenantSalesReport.id) return

    // NOTE: 送信値の組み立ては実行中フラグを立てる前に行う。
    // フラグを立てたあとに例外が出ると finally を通らず、フラグが立ったままになり
    // 以降まったく確定できなくなるため (Issue #3685)。
    const putValues = {
      tenantId: tenant.id,
      salesDateId: tenantSalesReport.id,
      forms: getDefiniteValues(),
    }

    inProgressRef.current = true
    setInProgress(true)
    try {
      await dispatch(putDefiniteValues(putValues)).unwrap()
      // NOTE: PreCheckに失敗したら、メモの登録はしない
      await callPutMessage()

      goToNextTenant('next')
    } catch {
      // NOTE: エラーメッセージは rejected を購読する snackbarSlice が表示する。
      // unwrap() は Error インスタンスではないオブジェクトを再 throw するため、
      // ここで捕捉しないと unhandledrejection として Bugsnag に非Errorが記録される。
    } finally {
      inProgressRef.current = false
      setInProgress(false)
    }
  }

  const handleClickBack = () => {
    if (showModal) return

    // NOTE: 一番最初の場合は、一覧に戻る
    if (selectedTenantIndex <= 0) {
      setShowModal(true)
      return
    }

    closeLogPanel()
    dispatch(decrementSelectedTenantIndex())
    dispatch(clearPutDefiniteValues())
    deselectImage()
    sendReportCheckEvent('back')

    formMethods.resetField(FormNameEnum.selectSalesDate)

    navigate(
      Path.tenantSalesReport(
        orgCode,
        storeCode,
        selectedTenantCodes[selectedTenantIndex - 1]
      )
    )
  }

  const isEnableToFinalizeAndFilterByMisMatchAndSendMessageAndSendNotificationByRoles =
    isOneOfRoles(
      AdminAdminSalesReportManagerEditorSalesReportManagerRoles,
      role
    )

  const handleClickSkip = async () => {
    if (showModal) return
    try {
      if (
        isEnableToFinalizeAndFilterByMisMatchAndSendMessageAndSendNotificationByRoles
      ) {
        await callPutMessage()
      }
      goToNextTenant('skip')
    } catch {
      // NOTE: エラーメッセージは rejected を購読する snackbarSlice が表示する。
      // unwrap() は Error インスタンスではないオブジェクトを再 throw するため、
      // ここで捕捉しないと unhandledrejection として Bugsnag に非Errorが記録される。
    } finally {
      setInProgress(false)
    }
  }

  const handleClickClose = () => {
    setShowModal(true)
  }

  const handleClickModalConfirm = () => {
    dispatch(
      clearGetTenantSalesReportByParams({
        orgCode,
        storeCode,
        tenantCode,
        date,
      })
    )
    goToSummariesPage()
  }

  const handleClickModalCancel = () => {
    setShowModal(false)
  }

  const callNotificationToTenantAPI = async (
    notification: string
  ): Promise<void> => {
    try {
      await dispatch(
        postNotificationToTenant({
          orgCode,
          storeCode,
          tenantCode,
          salesDate: params.date,
          salesDateId: tenantSalesReport?.id ?? '',
          content: notification,
        })
      ).unwrap()
    } catch (error) {
      toastContext.sendToast({
        variant: 'error',
        title: 'メッセージの送信に失敗しました',
      })
    }
  }

  const handleClickValue = (
    value: string,
    index: number,
    rowIndex: number,
    childIndex?: number
  ) => {
    if (childIndex === undefined) {
      formMethods.setValue(
        `${FormNameEnum.registers}.${index}.rows.${rowIndex}.value`,
        // @ts-ignore
        value
      )
      // NOTE: 小要素が一つの場合は、小要素の値も更新
      if (registers[index].rows[rowIndex].childRows.length === 1) {
        formMethods.setValue(
          `${
            FormNameEnum.registers
          }.${index}.rows.${rowIndex}.children.${0}.value`,
          // @ts-ignore
          value
        )
      }
    } else {
      formMethods.setValue(
        `${FormNameEnum.registers}.${index}.rows.${rowIndex}.children.${childIndex}.value`,
        // @ts-ignore
        value
      )
    }
  }

  const progressPercent =
    ((selectedTenantIndex + 1) / selectedTenantCodes.length) * 100

  return (
    <>
      <FormProvider {...formMethods}>
        <form>
          <TenantSalesReportTemplate
            isLoading={isLoading}
            isEnableToFinalizeAndFilterByMisMatchAndSendMessageAndSendNotificationByRoles={
              isEnableToFinalizeAndFilterByMisMatchAndSendMessageAndSendNotificationByRoles
            }
            orgCode={orgCode}
            store={store}
            tenant={tenant}
            reportStatus={tenantSalesReport?.reportStatus}
            tenantPath={Path.tenant(orgCode, storeCode, tenantCode)}
            date={formatDateWithDayOfWeek(params.date)}
            selectedDate={params.date}
            tenantDetail={tenantSalesReport?.tenantDetail}
            columns={getColumns(registers.length, orgCode)}
            registers={registers}
            disabledConfirmButton={disabledConfirmButton}
            onClickConfirm={handleClickConfirm}
            onClickBack={handleClickBack}
            onClickClose={handleClickClose}
            onClickSkip={handleClickSkip}
            showModal={showModal}
            reportMessages={reportMessages}
            notifications={tenantSalesReport?.notifications}
            role={role}
            selectOptions={selectOptions}
            selectPlaceholder={selectOptions[0]?.title}
            selectDefaultValue={selectOptions[0]?.value as string | undefined}
            progress={progressPercent}
            totalCount={selectedTenantCodes.length}
            currentCount={selectedTenantIndex + 1}
            selectedResentImage={selectedResentImage}
            selectedTenantCodes={selectedTenantCodes}
            onClickModalConfirm={handleClickModalConfirm}
            onClickModalCancel={handleClickModalCancel}
            onClickNotificationModalRegister={callNotificationToTenantAPI}
            onClickValue={handleClickValue}
            onClickHistoryButton={
              isActivityLogEnabled(orgCode) && tenantSalesReport?.id
                ? openLogPanel
                : undefined
            }
          />
        </form>
      </FormProvider>
      <ActivityLogPanel
        orgCode={orgCode}
        open={isLogPanelOpen}
        title="確定履歴"
        logs={activityLogs}
        isLoading={isActivityLogsLoading}
        isLoadingMore={isActivityLogsLoadingMore}
        hasMore={hasMoreActivityLogs}
        showDiff={false}
        dateRangeFilter={activityLogDateRangeFilter}
        errorMessage={activityLogError}
        onClose={closeLogPanel}
        onLoadMore={handleLoadMoreActivityLogs}
      />
    </>
  )
}

export default TenantSalesReport
